import { computed, Injectable, signal, Type } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, takeUntil } from 'rxjs';
import { ComponentEnum, OrderTypeEnum } from '@core/enums';
import { IAbstractPresenter, IBaseStrategy, ILanguageObject } from '@core/interfaces';
import { Schedule, Store as StoreModel } from '@core/models';
import { I18nService } from '@core/services';
import { selectLanguaje, selectOrderType, selectStore, selectStoreList } from '@store/selectors';
import {
	IStoresListCallback,
	IStoresListComponent,
	IStoresListPresenter,
	IStoresListStrategyDomain,
} from './interfaces';
import { StoreDeliveryCodeEnum } from './enums';
import { StoresListFactory } from './domain/stores-list.factory';
import { IStoreStatus, StoresProcessor, StoreScheduleRange } from '../../domain';

@Injectable()
export class StoresListPresenter
	extends IAbstractPresenter<IStoresListComponent>
	implements IStoresListPresenter
{
	translations: ILanguageObject;

	readonly orderTypeSignal = signal<string>(OrderTypeEnum.Pickup);
	readonly storesWithDistanceSignal = signal<StoreModel[]>([]);
	readonly storeInDeliverySignal = signal<StoreModel[]>([]);

	readonly selectedComponent = computed<Type<IStoresListStrategyDomain> | null>(() =>
		this.storesListFactory.getStrategy(this.orderTypeSignal())
	);

	readonly currentStores = computed<StoreModel[]>(() => {
		const type = this.orderTypeSignal();
		return type === OrderTypeEnum.Pickup || type === StoreDeliveryCodeEnum.Pickup
			? this.storesWithDistanceSignal()
			: this.storeInDeliverySignal();
	});

	readonly strategy: IBaseStrategy<IStoresListCallback> = {
		callback: {
			onSelectStore: (store: StoreModel, code: StoreDeliveryCodeEnum): void => {
				this.view?.onSelectStore(store, code);
			},
		},
	};

	readonly orderType$: Observable<string | null>;
	readonly storeList$: Observable<StoreModel[] | null>;
	readonly currentStore$: Observable<StoreModel | null>;

	constructor(
		private store: Store,
		private i18nService: I18nService,
		private storesProcessor: StoresProcessor,
		private storesListFactory: StoresListFactory
	) {
		super();
		this.translations = this.i18nService.getTranslations(ComponentEnum.MultiStore);

		this.orderType$ = this.store.select(selectOrderType);
		this.storeList$ = this.store.select(selectStoreList);
		this.currentStore$ = this.store.select(selectStore);
	}

	dispatchActions(): void {
		// Stores collections are managed reactively via inputs and store selectors
	}

	suscribeObservers(): void {
		this.store
			.select(selectLanguaje)
			.pipe(takeUntil(this.destroy$))
			.subscribe(lang => {
				this.translations = this.i18nService.getTranslations(ComponentEnum.MultiStore, lang);
				if (this.view) {
					this.view.translations = this.translations;
				}
				if (this.cdr) {
					this.cdr.detectChanges();
				}
			});

		this.orderType$.pipe(takeUntil(this.destroy$)).subscribe(type => {
			if (type) {
				this.orderTypeSignal.set(type);
			}
		});
	}

	setStoresWithDistance(stores: StoreModel[] | null): void {
		this.storesWithDistanceSignal.set(stores ?? []);
	}

	setStoreInDelivery(stores: StoreModel[] | null): void {
		this.storeInDeliverySignal.set(stores ?? []);
	}

	setOrderType(orderType: string | null): void {
		if (orderType) {
			this.orderTypeSignal.set(orderType);
		}
	}

	getRangeTimeByDay(
		store: StoreModel,
		code: StoreDeliveryCodeEnum | 'DELIVERY' | 'PICK-UP'
	): StoreScheduleRange | null {
		return this.storesProcessor.getRangeTimeByDay(store, code);
	}

	getStoreStatus(horarios: Schedule[]): IStoreStatus {
		return this.storesProcessor.getStoreStatus(horarios);
	}

	getStoreScheduleTime(
		store: StoreModel,
		code: StoreDeliveryCodeEnum | string,
		isAvailable: boolean
	): string {
		return this.storesProcessor.getStoreScheduleTime(store, code, isAvailable);
	}
}
